import { useState } from "react";
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { THEMES, useTheme, setTheme } from "@/lib/theme";
import { scrollToId } from "@/lib/scroll";
import { SERVICES, SILOS, STATIC_PAGES, equivalentPath, pathFor, staticPath } from "@/lib/siteMap";
import { ARTICLE_PAIRS } from "@/lib/blogData";
import { track } from "@/lib/analytics";
import { waLink } from "@/lib/wa";
import Magnetic from "./Magnetic";

export function Wordmark({ className = "" }) {
  const theme = useTheme();
  return (
    <span className={`inline-flex items-center ${className}`} data-testid="wordmark">
      <img src="/assets/logo-mark.png" alt="" aria-hidden="true" className="mr-[0.5em] h-[1.45em] w-auto" style={{ filter: theme.logoFilter }} />
      <span className="whitespace-nowrap font-display font-semibold tracking-tight">
        DEVOLU<span className="text-volt">Z</span>ION
      </span>
    </span>
  );
}

export function ThemePicker({ className = "" }) {
  const theme = useTheme();
  const { lang } = useLanguage();
  const labels = {
    blue: lang === "en" ? "Blue theme" : "Tema azul",
    green: lang === "en" ? "Green theme" : "Tema verde",
    pink: lang === "en" ? "Pink theme" : "Tema rosa",
  };
  return (
    <div className={`flex items-center gap-1.5 rounded-full border border-white/10 p-1 ${className}`} data-testid="theme-picker" role="group" aria-label={lang === "en" ? "Color theme" : "Color del tema"}>
      {Object.values(THEMES).map((t) => (
        <button
          key={t.id}
          type="button"
          data-testid={`theme-${t.id}`}
          aria-label={labels[t.id]}
          aria-pressed={theme.id === t.id}
          onClick={() => {
            setTheme(t.id);
            track("theme_change", { theme: t.id, language: lang });
          }}
          className="flex h-6 w-6 items-center justify-center rounded-full"
          style={{ backgroundColor: "transparent" }}
        >
          <span
            className={`block h-3.5 w-3.5 rounded-full transition-all duration-300 ${theme.id === t.id ? "ring-2 ring-white/70 ring-offset-2 ring-offset-ink scale-110" : "opacity-45 hover:opacity-100"}`}
            style={{ backgroundColor: t.dot }}
          />
        </button>
      ))}
    </div>
  );
}

function LangToggle({ className = "" }) {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const switchTo = (target) => {
    if (target === lang) return;
    track("language_change", { language: target, from_language: lang });
    navigate(equivalentPath(location.pathname, target, ARTICLE_PAIRS));
  };

  return (
    <div className={`flex items-center rounded-full border border-white/10 p-0.5 ${className}`} data-testid="language-toggle">
      {["es", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => switchTo(l)}
          className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
            lang === l ? "bg-white text-black" : "text-zinc-400 hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const [mobileServices, setMobileServices] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  const L = `/${lang}`;
  const isHome = location.pathname === `${L}/` || location.pathname === L;

  const go = (to) => { setOpen(false); setMega(false); navigate(to); };
  const goWork = () => {
    setOpen(false);
    if (isHome) scrollToId("casos");
    else navigate(`${L}/#casos`);
  };

  const mainLinks = [
    { label: t.ui.home, to: `${L}/`, testid: "nav-link-home" },
    { label: t.ui.pricing, to: staticPath(lang, "precios"), testid: "nav-link-pricing" },
    { label: t.ui.work, action: goWork, testid: "nav-link-cases" },
    { label: t.ui.about, to: staticPath(lang, "nosotros"), testid: "nav-link-about" },
    { label: t.ui.insights, to: staticPath(lang, "recursos"), testid: "nav-link-insights" },
    { label: t.ui.contact, to: staticPath(lang, "contacto"), testid: "nav-link-contact" },
  ];

  const megaData = SILOS.map((silo) => ({
    id: silo.id,
    name: silo[lang].name,
    items: [
      ...SERVICES.filter((s) => s.silo === silo.id).map((s) => ({ label: s[lang].menu, to: pathFor(lang, s.id) })),
      ...silo.extras.map((e) => ({ label: e[lang], to: pathFor(lang, e.to) })),
    ],
  }));

  return (
    <>
      <motion.header
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed inset-x-0 top-11 z-[80] transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled || mega ? "glass border-b border-white/5" : "border-b border-transparent"
        }`}
        data-testid="navbar"
        onMouseLeave={() => setMega(false)}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <button onClick={() => go(`${L}/`)} className="text-lg" data-testid="nav-logo" aria-label="DEVOLUZION home">
            <Wordmark />
          </button>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Principal">
            <button
              onClick={() => go(`${L}/`)}
              data-testid="nav-link-home"
              className="group relative font-mono text-[13px] uppercase tracking-[0.2em] text-zinc-200 transition-colors duration-300 hover:text-white"
            >
              {t.ui.home}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-volt transition-[width] duration-300 group-hover:w-full" />
            </button>
            <button
              onMouseEnter={() => setMega(true)}
              onClick={() => go(staticPath(lang, "servicios"))}
              data-testid="nav-link-services"
              className={`group relative flex items-center gap-1 font-mono text-[13px] uppercase tracking-[0.2em] transition-colors duration-300 ${mega ? "text-white" : "text-zinc-200 hover:text-white"}`}
            >
              {t.ui.services}
              <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${mega ? "rotate-180" : ""}`} />
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-volt transition-[width] duration-300 group-hover:w-full" />
            </button>
            {mainLinks.slice(1).map((l) => (
              <button
                key={l.testid}
                data-testid={l.testid}
                onClick={() => (l.action ? l.action() : go(l.to))}
                className="group relative font-mono text-[13px] uppercase tracking-[0.2em] text-zinc-200 transition-colors duration-300 hover:text-white"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-volt transition-[width] duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <ThemePicker />
            <LangToggle />
            <Magnetic strength={0.25} className="hidden md:block">
              <button
                data-testid="nav-cta"
                onClick={() => go(staticPath(lang, "contacto"))}
                className="group flex items-center gap-2 rounded-full bg-white px-5 py-2 text-[13px] font-medium text-black transition-colors duration-300 hover:bg-volt hover:text-onvolt"
              >
                {t.ui.letsTalk}
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-45" />
              </button>
            </Magnetic>
            <button className="lg:hidden text-white" onClick={() => setOpen(true)} data-testid="mobile-menu-button" aria-label="Abrir menú">
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mega && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-x-0 top-full hidden border-b border-white/5 bg-ink lg:block"
              data-testid="mega-menu"
              onMouseEnter={() => setMega(true)}
            >
              <div className="mx-auto grid max-w-7xl grid-cols-6 gap-8 px-6 py-10">
                {megaData.map((silo) => (
                  <div key={silo.id}>
                    <p className="font-mono text-[10px] tracking-[0.3em] text-volt">{silo.name}</p>
                    <ul className="mt-4 space-y-2.5">
                      {silo.items.map((item) => (
                        <li key={item.label}>
                          <button
                            onClick={() => go(item.to)}
                            data-testid={`mega-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="text-left text-[13px] leading-snug text-zinc-400 transition-colors duration-200 hover:text-white"
                          >
                            {item.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[85] flex flex-col bg-ink/95 backdrop-blur-2xl"
            data-testid="mobile-menu"
          >
            <div className="flex h-16 items-center justify-between px-6 pt-11">
              <Wordmark className="text-lg" />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Cerrar menú">
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-8 pb-8 pt-4">
              <motion.button
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                onClick={() => go(`${L}/`)} data-testid="mobile-nav-home"
                className="block w-full border-b border-white/5 py-4 text-left font-display text-3xl font-medium tracking-tight text-white"
              >
                {t.ui.home}
              </motion.button>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <button
                  onClick={() => setMobileServices(!mobileServices)}
                  data-testid="mobile-nav-services"
                  className="flex w-full items-center justify-between border-b border-white/5 py-4 text-left font-display text-3xl font-medium tracking-tight text-white"
                >
                  {t.ui.services}
                  <ChevronDown className={`h-5 w-5 transition-transform duration-300 ${mobileServices ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {mobileServices && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden border-b border-white/5"
                    >
                      <div className="space-y-5 py-5">
                        {megaData.map((silo) => (
                          <div key={silo.id}>
                            <p className="font-mono text-[10px] tracking-[0.3em] text-volt">{silo.name}</p>
                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                              {silo.items.map((item) => (
                                <button key={item.label} onClick={() => go(item.to)} className="text-sm text-zinc-400">
                                  {item.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                        <button onClick={() => go(staticPath(lang, "servicios"))} className="font-mono text-[11px] tracking-[0.2em] text-volt">
                          {t.ui.allServices} →
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
              {mainLinks.slice(1).map((l, i) => (
                <motion.button
                  key={l.testid}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 + i * 0.05 }}
                  onClick={() => (l.action ? l.action() : go(l.to))}
                  data-testid={`mobile-${l.testid}`}
                  className="block w-full border-b border-white/5 py-4 text-left font-display text-3xl font-medium tracking-tight text-white"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="mt-8 space-y-4">
                <div className="flex items-center gap-3">
                  <ThemePicker />
                  <LangToggle />
                </div>
                <a
                  href={waLink(lang, "general")}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="mobile-nav-whatsapp"
                  className="block rounded-full bg-[#25D366] py-4 text-center font-medium text-white"
                >
                  WhatsApp +56 9 3560 9185
                </a>
                <button
                  onClick={() => go(staticPath(lang, "contacto"))}
                  data-testid="mobile-nav-cta"
                  className="w-full rounded-full bg-white py-4 text-center font-medium text-black"
                >
                  {t.ui.letsTalk} →
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
